// File: frontend/src/views/public/PrivacyPolicy.js

import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Mail } from 'lucide-react';
import PageContainer from '../../components/layout/PageContainer';
import styles from './LegalPage.module.css';
import SEO from '../../components/utility/SEO';
import { SEO_CONFIG } from '../../config/seo';

const PrivacyPolicy = () => {
  const seo = SEO_CONFIG.privacy;

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO 
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.image}
      />
      <PageContainer>
        <div className={styles.page}>
          {/* Header */}
          <div className={styles.header}>
            <h1 className={styles.title}>Privacy Policy</h1>
            <p className={styles.effectiveDate}>Effective Date: February 10, 2026</p>
            <p className={styles.intro}>
              At Allegory, your privacy is not negotiable. This Privacy Policy explains how Allegory Technology Inc. ("Allegory," "we," "us," or "our") collects, uses, stores, and protects your personal information when you use our digital insurance wallet platform.
            </p>
          </div>

          {/* Table of Contents */}
          <nav className={styles.toc}>
            <h2 className={styles.tocTitle}>Table of Contents</h2>
            <ol className={styles.tocList}>
              <li><button onClick={() => scrollToSection('section-1')}>Information We Collect</button></li>
              <li><button onClick={() => scrollToSection('section-2')}>How We Use Your Information</button></li>
              <li><button onClick={() => scrollToSection('section-3')}>Data Storage and Security</button></li>
              <li><button onClick={() => scrollToSection('section-4')}>We Never Sell Your Data</button></li>
              <li><button onClick={() => scrollToSection('section-5')}>Third-Party Services</button></li>
              <li><button onClick={() => scrollToSection('section-6')}>Cookies and Tracking</button></li>
              <li><button onClick={() => scrollToSection('section-7')}>Your Rights and Choices</button></li>
              <li><button onClick={() => scrollToSection('section-8')}>Data Retention and Deletion</button></li>
              <li><button onClick={() => scrollToSection('section-9')}>SMS Verification and Consent</button></li>
              <li><button onClick={() => scrollToSection('section-10')}>Children's Privacy</button></li>
              <li><button onClick={() => scrollToSection('section-11')}>International Users</button></li>
              <li><button onClick={() => scrollToSection('section-12')}>Changes to This Policy</button></li>
              <li><button onClick={() => scrollToSection('section-13')}>Contact Us</button></li>
            </ol>
          </nav>

          {/* Content */}
          <div className={styles.content}>
            {/* Section 1 */}
            <section id="section-1" className={styles.section}>
              <h2 className={styles.sectionTitle}>1. Information We Collect</h2>
              
              <h3 className={styles.subsectionTitle}>1.1 Information You Provide</h3>
              <p>When you create an account or use our services, we collect:</p>
              <ul>
                <li><strong>Contact Information:</strong> First name, last name, email address, and phone number</li>
                <li><strong>Insurance Documents:</strong> PDF files of your insurance policies (auto, home, life, health, pet, travel, commercial, pension)</li>
                <li><strong>Policy Metadata:</strong> Information extracted from your policies by our AI, including coverage details, limits, deductibles, insurer names, policy numbers, and effective dates</li>
                <li><strong>Chat History:</strong> Conversations you have with our AI assistant about your policies</li>
                <li><strong>Payment Information:</strong> Billing details processed through Stripe (we do not store your credit card information)</li>
              </ul>

              <h3 className={styles.subsectionTitle}>1.2 Information We Collect Automatically</h3>
              <p>When you access our platform, we automatically collect:</p>
              <ul>
                <li><strong>Device Information:</strong> User agent, browser type, operating system, device identifiers</li>
                <li><strong>Usage Data:</strong> Pages viewed, features used, time spent on the platform, click patterns</li>
                <li><strong>IP Address:</strong> Your internet protocol address for security and regional data routing</li>
                <li><strong>Log Data:</strong> Server logs, error reports, and system diagnostics</li>
              </ul>

              <h3 className={styles.subsectionTitle}>1.3 Information from Third Parties</h3>
              <p>We may receive information from:</p>
              <ul>
                <li><strong>Insurance Carriers:</strong> Only if you explicitly request a quote through our platform, we share minimal information (policy type, coverage needs) to obtain pricing</li>
                <li><strong>Payment Processors:</strong> Transaction status and billing information from Stripe</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section id="section-2" className={styles.section}>
              <h2 className={styles.sectionTitle}>2. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul>
                <li><strong>Provide Our Services:</strong> Store your policies, analyze coverage, answer questions, and provide renewal alerts</li>
                <li><strong>Authenticate Your Account:</strong> Verify your identity through passwordless SMS-based authentication</li>
                <li><strong>Process Payments:</strong> Handle subscription billing and refunds</li>
                <li><strong>Improve Our Platform:</strong> Analyze usage patterns to enhance features and user experience</li>
                <li><strong>Communicate with You:</strong> Send service updates, renewal alerts, and respond to support requests</li>
                <li><strong>Ensure Security:</strong> Detect fraud, prevent abuse, and protect against malicious activity</li>
                <li><strong>Comply with Legal Obligations:</strong> Meet regulatory requirements and respond to lawful requests</li>
              </ul>
              <p className={styles.highlight}>
                <strong>We do not use your personal information to train AI models.</strong> Your policies and conversations remain private and are never used to improve models for other users.
              </p>
            </section>

            {/* Section 3 */}
            <section id="section-3" className={styles.section}>
              <h2 className={styles.sectionTitle}>3. Data Storage and Security</h2>
              
              <h3 className={styles.subsectionTitle}>3.1 Where We Store Your Data</h3>
              <p>Your data is stored on secure Amazon Web Services (AWS) infrastructure:</p>
              <ul>
                <li><strong>Canadian Users:</strong> Data is stored in AWS Canada (ca-central-1)</li>
                <li><strong>US Users:</strong> Data is stored in AWS US East (us-east-1)</li>
                <li><strong>Website Visitors:</strong> By default, visitor data is processed on servers in Canada</li>
              </ul>
              <p>We do not currently offer services to users outside Canada and the United States.</p>

              <h3 className={styles.subsectionTitle}>3.2 How We Protect Your Data</h3>
              <ul>
                <li><strong>Encryption at Rest:</strong> All uploaded documents are encrypted using AES-256 encryption with AWS Key Management Service (KMS)</li>
                <li><strong>Encryption in Transit:</strong> All data transmitted between your device and our servers is protected by TLS/SSL encryption</li>
                <li><strong>Access Controls:</strong> Role-based access ensures only authorized personnel can access sensitive data</li>
                <li><strong>Server-Side Encryption:</strong> AWS S3 server-side encryption protects stored files</li>
                <li><strong>Regular Security Audits:</strong> We continuously monitor for vulnerabilities and unauthorized access</li>
              </ul>

              <h3 className={styles.subsectionTitle}>3.3 Data Processing with AI Providers</h3>
              <p>For certain AI tasks, we may use third-party providers such as Anthropic, OpenAI, or Cohere. In these cases:</p>
              <ul>
                <li>Data is retained by these providers for <strong>no more than 30 days</strong></li>
                <li>We use our own AI models whenever possible, processed in Canada or the US</li>
                <li>No personal information is used to train third-party models</li>
              </ul>
            </section>

            {/* Section 4 - HIGHLIGHTED */}
            <section id="section-4" className={`${styles.section} ${styles.highlightSection}`}>
              <div className={styles.highlightBadge}>
                <Eye size={24} strokeWidth={2} />
              </div>
              <h2 className={styles.sectionTitle}>4. We Never Sell Your Data</h2>
              <p className={styles.highlightText}>
                <strong>We do not sell, rent, or trade your personal information. Period.</strong>
              </p>
              <p>
                We make money from subscriptions, not data sales. Your policies, conversations, and personal details are yours. We will never share your personally identifiable information with insurance companies, data brokers, advertisers, or any third party for monetary gain.
              </p>
              <p>
                We may aggregate and anonymize data for internal analytics or industry research, but this data cannot be traced back to you.
              </p>
            </section>

            {/* Section 5 */}
            <section id="section-5" className={styles.section}>
              <h2 className={styles.sectionTitle}>5. Third-Party Services</h2>
              <p>We use the following third-party services to operate our platform:</p>
              
              <h3 className={styles.subsectionTitle}>5.1 Amazon Web Services (AWS)</h3>
              <ul>
                <li><strong>Purpose:</strong> Cloud infrastructure, data storage (S3), email delivery (SES), SMS delivery (SNS)</li>
                <li><strong>Data Shared:</strong> All user data is stored on AWS servers</li>
                <li><strong>Privacy Policy:</strong> <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer">aws.amazon.com/privacy</a></li>
              </ul>

              <h3 className={styles.subsectionTitle}>5.2 Stripe</h3>
              <ul>
                <li><strong>Purpose:</strong> Payment processing for subscriptions</li>
                <li><strong>Data Shared:</strong> Billing information, transaction details (we do not store credit card numbers)</li>
                <li><strong>Privacy Policy:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
              </ul>

              <h3 className={styles.subsectionTitle}>5.3 AI Providers (Anthropic, OpenAI, Cohere)</h3>
              <ul>
                <li><strong>Purpose:</strong> Policy analysis, natural language processing</li>
                <li><strong>Data Shared:</strong> Policy text and user questions (anonymized when possible)</li>
                <li><strong>Data Retention:</strong> Maximum 30 days</li>
              </ul>

              <h3 className={styles.subsectionTitle}>5.4 AllegoryOS (In-House Analytics)</h3>
              <p>
                We do not use third-party analytics tools like Google Analytics or Facebook Pixel. All usage analytics are processed in-house through our proprietary AllegoryOS platform.
              </p>
            </section>

            {/* Section 6 */}
            <section id="section-6" className={styles.section}>
              <h2 className={styles.sectionTitle}>6. Cookies and Tracking</h2>
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li><strong>Essential Cookies:</strong> Maintain your session, remember your login, and ensure platform functionality</li>
                <li><strong>Analytics Cookies:</strong> Track usage patterns to improve our services (processed in-house, not shared with third parties)</li>
              </ul>
              <p>
                We do not use marketing cookies, advertising trackers, or third-party analytics tools. You can manage cookie preferences through our Cookie Consent banner or your browser settings.
              </p>
            </section>

            {/* Section 7 */}
            <section id="section-7" className={styles.section}>
              <h2 className={styles.sectionTitle}>7. Your Rights and Choices</h2>
              <p>You have the following rights regarding your personal information:</p>

              <h3 className={styles.subsectionTitle}>7.1 Access and Portability</h3>
              <p>You can download all your data (policies, chat history, account information) at any time from your account settings.</p>

              <h3 className={styles.subsectionTitle}>7.2 Correction</h3>
              <p>You can update your personal information (name, email, phone number) directly in your account settings or by contacting us.</p>

              <h3 className={styles.subsectionTitle}>7.3 Deletion</h3>
              <p>You can delete your account at any time. Upon deletion:</p>
              <ul>
                <li>Your account is immediately deactivated</li>
                <li>All personal data is permanently deleted within <strong>30 days</strong></li>
                <li>Uploaded policies, chat history, and metadata are irrecoverably erased</li>
                <li>Analytics data is anonymized and cannot be traced back to you</li>
              </ul>

              <h3 className={styles.subsectionTitle}>7.4 Opt-Out of Marketing</h3>
              <p>You can unsubscribe from marketing emails at any time by clicking the "Unsubscribe" link in any email or updating your preferences in account settings.</p>

              <h3 className={styles.subsectionTitle}>7.5 Opt-Out of SMS</h3>
              <p>
                You can opt out of SMS notifications, but note that SMS verification is required for passwordless authentication. If you opt out, you will not be able to sign in or recover your account using your phone number.
              </p>
            </section>

            {/* Section 8 */}
            <section id="section-8" className={styles.section}>
              <h2 className={styles.sectionTitle}>8. Data Retention and Deletion</h2>
              
              <h3 className={styles.subsectionTitle}>8.1 Active Accounts</h3>
              <p>We retain your data for as long as your account is active and as needed to provide our services.</p>

              <h3 className={styles.subsectionTitle}>8.2 Deleted Accounts</h3>
              <p>When you delete your account:</p>
              <ul>
                <li>You have <strong>30 days</strong> to change your mind and reactivate your account</li>
                <li>After 30 days, all personal data is permanently deleted from our servers</li>
                <li>We do not retain backups of deleted user data</li>
              </ul>

              <h3 className={styles.subsectionTitle}>8.3 Analytics and Logs</h3>
              <ul>
                <li><strong>Server Logs:</strong> Detailed traffic logs are retained for 7 days</li>
                <li><strong>System Logs:</strong> CloudWatch logs are retained for 30 days</li>
                <li><strong>Analytics Data:</strong> After account deletion, analytics data is anonymized and cannot be linked to you</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section id="section-9" className={styles.section}>
              <h2 className={styles.sectionTitle}>9. SMS Verification and Consent</h2>
              <p>
                Allegory uses a passwordless authentication system that requires SMS verification. By creating an account, you consent to receive a one-time verification code via SMS to your phone number.
              </p>

              <h3 className={styles.subsectionTitle}>9.1 How SMS Verification Works</h3>
              <p>
                Allegory uses a passwordless authentication system that sends one-time passcode (OTP)
                SMS messages in two scenarios:
              </p>
              <ul>
                <li>
                  <strong>Account Creation:</strong> When you create an Allegory account, you provide
                  express written consent via a mandatory checkbox before any SMS is sent. The checkbox
                  is unchecked by default and must be actively selected.
                </li>
                <li>
                  <strong>Sign-In via Phone:</strong> When you choose to sign in using your phone number,
                  a consent notice is displayed on screen before you submit your number. By continuing,
                  you consent to receive a one-time verification code for that sign-in attempt.
                </li>
              </ul>
              <p>
                In both cases, SMS messages are sent only after you have provided explicit consent and
                only in direct response to an action you initiated. SMS delivery is handled by AWS
                Simple Notification Service (SNS) via toll-free number{' '}
                <a href="tel:+18333706794">+1 (833) 370-6794</a>. We do not send marketing messages
                via SMS.
              </p>

              <h3 className={styles.subsectionTitle}>9.2 Opting Out of SMS</h3>
              <p>
                You can opt out of SMS notifications, but you will not be able to sign in or recover
                your account without SMS verification. If you wish to stop receiving SMS, you must
                delete your account.
              </p>

              <h3 className={styles.subsectionTitle}>9.3 Carrier Compliance</h3>
              <p>
                We comply with all US and Canadian telecommunications regulations, including TCPA
                (Telephone Consumer Protection Act) and CASL (Canada's Anti-Spam Legislation). SMS
                messages are sent only for authentication purposes and with your explicit consent.
              </p>

              <h3 className={styles.subsectionTitle}>9.4 SMS Data Sharing</h3>
              <p>
                SMS opt-in data and consent will not be shared with any third parties. This expressly
                excludes SMS opt-in information from any data sharing practices described elsewhere
                in this policy.
              </p>
            </section>

            {/* Section 10 */}
            <section id="section-10" className={styles.section}>
              <h2 className={styles.sectionTitle}>10. Children's Privacy</h2>
              <p>
                Our services are not intended for minors. However, we recognize that insurance policies may be held by individuals as young as 15 or 16 in certain jurisdictions where driver's licenses are issued at that age.
              </p>
              <p>
                <strong>Eligibility:</strong> You must be at least 15 years old (or the minimum age to hold a driver's license in your jurisdiction) to use our services. If you are under 18, you must have parental or guardian consent.
              </p>
              <p>
                We do not knowingly collect personal information from children under 13. If we discover that we have inadvertently collected such information, we will delete it immediately.
              </p>
            </section>

            {/* Section 11 */}
            <section id="section-11" className={styles.section}>
              <h2 className={styles.sectionTitle}>11. International Users</h2>
              
              <h3 className={styles.subsectionTitle}>11.1 Service Availability</h3>
              <p>
                Allegory currently provides services only to users in <strong>Canada and the United States</strong>. Users from other countries may access our website but cannot create accounts or upload policies.
              </p>

              <h3 className={styles.subsectionTitle}>11.2 PIPEDA Compliance (Canada)</h3>
              <p>
                We comply with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA). Canadian users have the right to access, correct, and delete their personal information as outlined in this policy.
              </p>

              <h3 className={styles.subsectionTitle}>11.3 CCPA Compliance (California)</h3>
              <p>
                California residents have additional rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul>
                <li><strong>Right to Know:</strong> You can request details about the personal information we collect and how we use it</li>
                <li><strong>Right to Delete:</strong> You can request deletion of your personal information</li>
                <li><strong>Right to Opt-Out:</strong> We do not sell personal information, so there is nothing to opt out of</li>
                <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights</li>
              </ul>
              <p>
                To exercise your CCPA rights, contact us at <a href="mailto:privacy@allegoryinsurance.com">privacy@allegoryinsurance.com</a>.
              </p>

              <h3 className={styles.subsectionTitle}>11.4 GDPR (European Union)</h3>
              <p>
                We do not offer services to EU residents. If you are located in the EU and access our website, your data is processed in Canada under this Privacy Policy.
              </p>
            </section>

            {/* Section 12 */}
            <section id="section-12" className={styles.section}>
              <h2 className={styles.sectionTitle}>12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make changes:
              </p>
              <ul>
                <li>We will update the "Effective Date" at the top of this page</li>
                <li>We will notify you via email if the changes are material</li>
                <li>Your continued use of our services after changes take effect constitutes acceptance of the updated policy</li>
              </ul>
              <p>
                We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            {/* Section 13 */}
            <section id="section-13" className={styles.section}>
              <h2 className={styles.sectionTitle}>13. Contact Us</h2>
              <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us:
              </p>
              <div className={styles.contactInfo}>
                <p><strong>Allegory Technology Inc.</strong></p>
                <p>C/O Allegory Data Protection Officer</p>
                <p>Toronto Exchange Tower</p>
                <p>130 King Street West, Suite 1900</p>
                <p>Toronto, ON, M5X 1E3, Canada</p>
                <p>Email: <a href="mailto:privacy@allegoryinsurance.com">privacy@allegoryinsurance.com</a></p>
              </div>
              <p>
                We will respond to your inquiry within 30 days.
              </p>
            </section>
          </div>

          {/* Footer CTA */}
          <div className={styles.footerCTA}>
            <p>Have questions about how we protect your data?</p>
            <Link to="/contact" className={styles.ctaButton}>
              <Mail size={20} strokeWidth={2} />
              Contact Us
            </Link>
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default PrivacyPolicy;